import { __awaiter, __generator } from "tslib";
import { MicroSyncMessage } from "@augloop/session-protocol";
import { SchemaObject, AddOperation, UpdateOperation, } from "@augloop/types-core";
import { VoiceTile, VoiceOperation, BatchTranscriptionConversation, } from "@augloop-types/voice";
import { AUDIO_SAMPLE_RATE } from "./AudioUtils";
import { ULS, TraceLevel, LogCategory } from "@1js/office-online-telemetry";
/** The ever-incrementing value for `seq` on voice tiles. */
var tileSequence = 0;
export var AugloopError;
(function (AugloopError) {
    AugloopError["ErrorAugloopActivateAnnotations"] = "ErrorAugloopActivateAnnotations";
})(AugloopError || (AugloopError = {}));
// Activate SpeechToText annotations in our AL runtime manager.
// Returns the activation token.
export var activateAnnotationAsync = function (alRuntimeManager, annotationTypeName, annotationObserver, enableSetAnnotationsFromALVoiceClient) { return __awaiter(void 0, void 0, void 0, function () {
    var activationResult, _a;
    return __generator(this, function (_b) {
        switch (_b.label) {
            case 0:
                ULS.sendTraceTag(0x208d13e0 /* tag_69rp6 */, LogCategory.msoulscat_Wac_Dictation, TraceLevel.Info, "Start to activate annotations of: " + annotationTypeName);
                if (!enableSetAnnotationsFromALVoiceClient) return [3 /*break*/, 2];
                return [4 /*yield*/, alRuntimeManager.activateAnnotation({
                        annotationType: annotationTypeName,
                        handler: function (operation) {
                            if (annotationObserver) {
                                annotationObserver.onAnnotationResult(operation);
                            }
                        },
                    })];
            case 1:
                _a = _b.sent();
                return [3 /*break*/, 4];
            case 2: return [4 /*yield*/, alRuntimeManager.activateAnnotation({
                    annotationType: annotationTypeName,
                })];
            case 3:
                _a = _b.sent();
                _b.label = 4;
            case 4:
                activationResult = _a;
                return [2 /*return*/, activationResult.token];
        }
    });
}); };
export var releaseAnnotations = function (alRuntimeManager, activationTokens) {
    activationTokens.map(function (token) {
        ULS.sendTraceTag(0x208d13e1 /* tag_69rp7 */, LogCategory.msoulscat_Wac_Dictation, TraceLevel.Info, "Start to release annotation of: " + token);
        // tslint:disable-next-line: no-floating-promises (TODO: verify we can let this promise float here)
        alRuntimeManager.releaseAnnotation({ token: token }); // eslint-disable-line @typescript-eslint/no-floating-promises
    });
};
/** Send an empty tile to ensure connection to AL and our audio processor, so we do not have to wait on mic permissions and the audio buffer to fill */
export var startAudioProcessor = function (alRuntimeManager, useFrontdoorWorkflow, dictationSettings, responseVersion, speechToTextProfile) {
    var dictationLanguage = dictationSettings.dictationLanguage, useAutoPunctuation = dictationSettings.useAutoPunctuation, useCorrections = dictationSettings.useCorrections, useVoiceCommands = dictationSettings.useVoiceCommands;
    ULS.sendTraceTag(0x200c331b /* tag_6ddm1 */, LogCategory.msoulscat_Wac_Dictation, TraceLevel.Info, "Sending empty warm-up tile, seq: ".concat(tileSequence, ", dictationLanguage: ").concat(dictationLanguage, ", useAutoPunctuation: ").concat(useAutoPunctuation, ", useCorrections: ").concat(useCorrections, ", useVoiceCommands: ").concat(useVoiceCommands));
    var voiceTile = new VoiceTile({
        sampleRate: AUDIO_SAMPLE_RATE,
        useFrontdoorWorkflow: useFrontdoorWorkflow,
        seq: tileSequence,
        dictationSettings: dictationSettings,
        responseVersion: responseVersion,
        speechToTextProfile: speechToTextProfile,
        commandSet: ["warm-up"],
    });
    var voiceOperation = new VoiceOperation({
        parentPath: ["session"],
        items: [
            {
                id: tileSequence.toString(),
                body: voiceTile,
            },
        ],
    });
    alRuntimeManager.submitOperation({ operation: voiceOperation });
    tileSequence += 1;
};
/** Send empty closing session */
export var endAudioProcessor = function (alRuntimeManager, useFrontdoorWorkflow, dictationSettings) {
    ULS.sendTraceTag(0x200c331c /* tag_6ddm2 */, LogCategory.msoulscat_Wac_Dictation, TraceLevel.Info, "Sending empty tile with endVoiceSession:true, seq: ".concat(tileSequence));
    var voiceTile = new VoiceTile({
        endVoiceSession: true,
        sampleRate: AUDIO_SAMPLE_RATE,
        useFrontdoorWorkflow: useFrontdoorWorkflow,
        seq: tileSequence,
        dictationSettings: dictationSettings,
    });
    var voiceOperation = new VoiceOperation({
        parentPath: ["session"],
        items: [
            {
                id: tileSequence.toString(),
                body: voiceTile,
            },
        ],
    });
    alRuntimeManager.submitOperation({ operation: voiceOperation });
    tileSequence += 1;
};
// Create and submit a voice tile with the provided audio data.
export var submitVoiceTile = function (alRuntimeManager, voiceData, audioSampleRate, useFrontdoorWorkflow, dictationSettings, responseVersion, speechToTextProfile, sessionTelemetry, commandSet) {
    if (commandSet === void 0) { commandSet = []; }
    var microSync = new MicroSyncMessage({
        item: {
            id: tileSequence.toString(),
            body: new VoiceTile({
                sampleRate: audioSampleRate,
                useFrontdoorWorkflow: useFrontdoorWorkflow,
                seq: tileSequence,
                dictationSettings: dictationSettings,
                responseVersion: responseVersion,
                data: new Uint8Array(voiceData.buffer, voiceData.byteOffset, voiceData.byteLength),
                speechToTextProfile: speechToTextProfile,
                commandSet: commandSet,
            }),
        },
    });
    // This will produce an error when a confirmation response is not returned from the client
    // We are currently logging this, but in the future we should likely end dictation after a number
    // of consecutive errors produced here indicating AL is not responding to our messages
    // eslint-disable-next-line  @typescript-eslint/no-floating-promises
    alRuntimeManager.submitCustomMessage({ message: microSync }).catch(function (err) {
        var _a, _b;
        // keep a count of errors per session
        if (sessionTelemetry === null || sessionTelemetry === void 0 ? void 0 : sessionTelemetry.onTileSubmitFailed) {
            sessionTelemetry.onTileSubmitFailed();
        }
        var errMsg = (_b = (_a = err === null || err === void 0 ? void 0 : err.error) !== null && _a !== void 0 ? _a : err === null || err === void 0 ? void 0 : err.message) !== null && _b !== void 0 ? _b : err === null || err === void 0 ? void 0 : err.toString();
        switch (errMsg) {
            case "Timeout waiting for response":
            case "Cancelled":
                // these messages mean network conditions are fragile - no need to trace these
                return;
            default:
                // trace anything else
                ULS.sendTraceTag(0x200c331d /* tag_6ddm3 */, LogCategory.msoulscat_Wac_Dictation, TraceLevel.Error, "submitVoiceTile error: ".concat(errMsg));
        }
    });
    tileSequence += 1;
};
export function submitBatchTranscriptionConversation(alRuntimeManager, conversationId, tileId, speechFDUrl) {
    var operation = new AddOperation({
        parentPath: ["session"],
        items: [
            {
                id: tileId,
                body: new BatchTranscriptionConversation({
                    conversationId: conversationId,
                    speechFDUrl: speechFDUrl,
                }),
            },
        ],
    });
    alRuntimeManager.submitOperation({ operation: operation });
}
// Given an operation, filter items by annotation type Add Operation or Update Operation.
export var filterOperationByAnnotationTypeName = function (operation, annotationTypeName) {
    if (SchemaObject.getTypeNameFor(operation) !== AddOperation.getTypeName() &&
        SchemaObject.getTypeNameFor(operation) !== UpdateOperation.getTypeName()) {
        return [];
    }
    if (operation.items.length === 0)
        return [];
    var filteredItems = operation.items.filter(function (item) {
        return item.body && SchemaObject.getTypeNameFor(item.body) === annotationTypeName;
    });
    return filteredItems;
};
//# sourceMappingURL=AugLoopUtils.js.map